import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import adminApi from "../../config/adminApi";
import { downloadAdminCsv } from "../../utils/adminCsvDownload";

const TABS = ["users", "scans", "videos"];

const DEFAULT_START = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
})();

const DEFAULT_END = new Date().toISOString().slice(0, 10);

const PAGE_SIZE = 50;

function CdpAdmin() {
  const navigate = useNavigate();

  const [tab, setTab] = useState("users");
  const [startDate, setStartDate] = useState(DEFAULT_START);
  const [endDate, setEndDate] = useState(DEFAULT_END);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [csvLoading, setCsvLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const endpointMap = {
        users: `/analytics/cdp-users?startDate=${startDate}&endDate=${endDate}&page=${page}&limit=${PAGE_SIZE}`,
        scans: `/analytics/cdp-scans?startDate=${startDate}&endDate=${endDate}&page=${page}&limit=${PAGE_SIZE}`,
        videos: `/analytics/cdp-videos?startDate=${startDate}&endDate=${endDate}&page=${page}&limit=${PAGE_SIZE}`,
      };
      const endpoint = endpointMap[tab];
      const res = await adminApi.get(endpoint);
      setRows(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, [tab, startDate, endDate, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTabChange = (t) => {
    setTab(t);
    setPage(1);
    setRows([]);
  };

  const handleFilter = (e) => {
    e.preventDefault();
    setPage(1);
    fetchData();
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminRole");
    navigate("/admin/login", { replace: true });
  };

  const handleCsvDownload = async () => {
    try {
      setCsvLoading(true);
      const pathMap = {
        users: `/analytics/cdp-users-csv?startDate=${startDate}&endDate=${endDate}`,
        scans: `/analytics/cdp-scans-csv?startDate=${startDate}&endDate=${endDate}`,
        videos: `/analytics/cdp-videos-csv?startDate=${startDate}&endDate=${endDate}`,
      };
      const filenameMap = {
        users: `cdp_users_${startDate}_to_${endDate}.csv`,
        scans: `cdp_scans_${startDate}_to_${endDate}.csv`,
        videos: `cdp_videos_${startDate}_to_${endDate}.csv`,
      };
      const path = pathMap[tab];
      const filename = filenameMap[tab];
      await downloadAdminCsv(path, filename);
    } catch (err) {
      alert("CSV download failed: " + err.message);
    } finally {
      setCsvLoading(false);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const columns = rows.length > 0 ? Object.keys(rows[0]) : [];
  const defaultColumnsMap = {
    users: [
      "userId",
      "name",
      "mobile",
      "email",
      "state",
      "language",
      "consentGiven",
      "otpVerified",
      "registeredAt",
      "utmSource",
      "utmMedium",
      "utmCampaign",
      "deviceManufacturer",
      "os",
      "deviceModel",
      "browser",
      "deviceType",
      "sessionCount",
      "dob",
      "gender",
      "familySize",
      "numberOfChildren",
      "childrenAges",
      "childrenAgeGroups",
      "totalScans",
      "totalCredits",
      "totalCashback",
      "cashbackStatus",
      "cashbackClaimedCount",
      "lastCashbackStatus",
      "lastCashbackAmount",
      "lastCashbackDate",
      "kuralsAssigned",
      "kuralsUnlocked",
      "kuralCompletionStatus",
      "scannedKurals",
      "typedKurals",
      "inputMethodBreakdown",
      "videosSubmitted",
      "videoRecordsCount",
      "prizeBrand",
      "prizeFranchise",
      "prizeElement",
      "prizeMrp",
      "prizeFulfilmentStatus",
      "dispatchName",
      "dispatchPhone",
      "dispatchAddress",
      "scansInRange",
      "skuShift",
      "hobbies",
      "favouriteDessert",
      "lastScanAt",
      "isPackScan",
    ],
    scans: [
      "machineCodeId",
      "userId",
      "mobile",
      "machineCode",
      "machineCodeValidationStatus",
      "skuCode",
      "skuName",
      "packMrp",
      "credits",
      "scannedAt",
      "scanHourOfDay",
    ],
    videos: [
      "userId",
      "mobile",
      "kuralId",
      "videoUrl",
      "durationSec",
      "submittedAt",
      "weeklySubmissionCount",
      "moderationStatus",
      "videoScoreTotal",
      "videoScoreExpression",
      "videoScoreVoice",
      "videoScoreCreativity",
      "videoScoreBodyLanguage",
    ],
  };

  const displayColumns = columns.length > 0 ? columns : defaultColumnsMap[tab] || [];

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <span style={s.headerTitle}>Britannia CDP Dashboard</span>
        <button style={s.logoutBtn} onClick={handleLogout}>
          Logout
        </button>
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        {TABS.map((t) => (
          <button
            key={t}
            style={{ ...s.tabBtn, ...(tab === t ? s.tabActive : {}) }}
            onClick={() => handleTabChange(t)}
          >
            {t === "users" ? "Users" : t === "scans" ? "Machine Scans" : "Videos"}
          </button>
        ))}
      </div>

      {/* Filters */}
      <form style={s.filterRow} onSubmit={handleFilter}>
        <label style={s.filterLabel}>
          From&nbsp;
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            style={s.dateInput}
          />
        </label>
        <label style={s.filterLabel}>
          To&nbsp;
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            style={s.dateInput}
          />
        </label>
        <button type="submit" style={s.filterBtn} disabled={loading}>
          {loading ? "Loading…" : "Apply"}
        </button>
        <button
          type="button"
          style={s.csvBtn}
          onClick={handleCsvDownload}
          disabled={csvLoading || loading}
        >
          {csvLoading ? "Downloading…" : "Export CSV"}
        </button>
      </form>

      {/* Summary */}
      <div style={s.summary}>
        Total: <strong>{total}</strong> records
      </div>

      {/* Error */}
      {error && <p style={s.error}>{error}</p>}

      {/* Table */}
      <div style={s.tableWrap}>
        {loading ? (
          <p style={s.loadingMsg}>Loading data…</p>
        ) : rows.length === 0 ? (
          <p style={s.loadingMsg}>No records found for this range.</p>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                {displayColumns.map((col) => (
                  <th key={col} style={s.th}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} style={i % 2 === 0 ? s.trEven : s.trOdd}>
                  {displayColumns.map((col) => (
                    <td key={col} style={s.td}>
                      {String(row[col] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={s.pagination}>
          <button
            style={s.pageBtn}
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => p - 1)}
          >
            &laquo; Prev
          </button>
          <span style={s.pageInfo}>
            Page {page} / {totalPages}
          </span>
          <button
            style={s.pageBtn}
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Next &raquo;
          </button>
        </div>
      )}
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh",
    background: "#f4f6f8",
    fontFamily: "sans-serif",
    fontSize: 14,
  },
  header: {
    background: "#c0392b",
    color: "#fff",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitle: { fontSize: 18, fontWeight: 700 },
  logoutBtn: {
    background: "rgba(255,255,255,0.2)",
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.5)",
    borderRadius: 4,
    padding: "6px 14px",
    cursor: "pointer",
    fontSize: 13,
  },
  tabs: {
    display: "flex",
    borderBottom: "2px solid #ddd",
    background: "#fff",
    padding: "0 24px",
  },
  tabBtn: {
    padding: "12px 20px",
    border: "none",
    background: "none",
    cursor: "pointer",
    fontSize: 14,
    color: "#555",
    fontWeight: 500,
    borderBottom: "2px solid transparent",
    marginBottom: -2,
  },
  tabActive: { color: "#c0392b", borderBottomColor: "#c0392b", fontWeight: 700 },
  filterRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "16px 24px",
    background: "#fff",
    borderBottom: "1px solid #eee",
    flexWrap: "wrap",
  },
  filterLabel: { fontSize: 13, color: "#555" },
  dateInput: {
    padding: "6px 10px",
    border: "1px solid #ccc",
    borderRadius: 4,
    fontSize: 13,
  },
  filterBtn: {
    padding: "8px 16px",
    background: "#2c3e50",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 13,
  },
  csvBtn: {
    padding: "8px 16px",
    background: "#27ae60",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 13,
  },
  summary: { padding: "8px 24px", color: "#555", fontSize: 13 },
  error: { color: "#c0392b", padding: "0 24px", fontSize: 13 },
  tableWrap: {
    overflowX: "auto",
    padding: "0 24px 24px",
  },
  loadingMsg: { padding: "24px 0", textAlign: "center", color: "#888" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    background: "#fff",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    borderRadius: 6,
    overflow: "hidden",
    marginTop: 8,
  },
  th: {
    padding: "10px 12px",
    background: "#2c3e50",
    color: "#fff",
    textAlign: "left",
    whiteSpace: "nowrap",
    fontSize: 13,
  },
  td: {
    padding: "8px 12px",
    borderBottom: "1px solid #f0f0f0",
    whiteSpace: "nowrap",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  trEven: { background: "#fff" },
  trOdd: { background: "#fafafa" },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    padding: "16px 0",
  },
  pageBtn: {
    padding: "8px 16px",
    background: "#2c3e50",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 13,
  },
  pageInfo: { fontSize: 13, color: "#555" },
};

export default CdpAdmin;
